/**
 * Category 1-line summary formatter — composes the sub-text shown beneath
 * each category name on the Page 3 Category Scores table (Sub-piece 4).
 *
 * Reads canonical CheckResult §A1 fields (check_number, ratio_pct,
 * severity_findings, plus aux passthroughs).
 *
 * Deterministic — same category result always produces the same summary.
 *
 * @module categorySummary
 */

import { buildComparisonCategorySummary } from '../shared/baselineComparisonDisplay.js';

const MAX_LEN = 85;

function pct(value, decimals = 1) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '0%';
  return `${value.toFixed(decimals)}%`;
}

function findCheck(category, checkNumber) {
  // D-SummaryHiddenCount (v3.23): a stage-hidden check contributes NO phrase to
  // the category summary — the summary must never name an assessment that did
  // not run (e.g. hidden #26 "0 behind data date" on a zero-actuals baseline,
  // where #26 is hidden inside the visible Calendar & Dates category). This is
  // prose composition only; the hidden-flag logic itself is untouched, and it
  // mirrors the per-check hidden filter the dashboard (CategoryExpanded) and the
  // PDF (check-block-assembler) already apply to the check LIST.
  return (category.checks || []).find((c) => c.check_number === checkNumber && !c.hidden);
}

function findingsCount(check) {
  // FIX-B: prefer the true total — on the bounded dashboard.json projection
  // the severity_findings array is capped (Stage 5.2) and its sibling
  // `severity_findings_total` carries the real length. On a full analysis the
  // sibling is absent and the array length IS the total, so PDF output is
  // byte-identical. (The dashboard CategoryRow now consumes this formatter —
  // Rule 12: one summary builder for both mirrors.)
  return check?.severity_findings_total ?? (check?.severity_findings?.length || 0);
}

function truncate(s) {
  if (typeof s !== 'string') return '';
  if (s.length <= MAX_LEN) return s;
  return s.slice(0, MAX_LEN - 1).trimEnd() + '\u2026';
}

/**
 * Compose a 1-line summary string for a single category.
 *
 * @param {object} category - Per-category result object (analysis.categories[i]).
 *   Expected shape: { id, name, score, hidden, checks: [<canonical CheckResult>] }
 * @returns {string} Summary string, ≤85 chars including ellipsis.
 */
export function getCategorySummary(category) {
  if (!category || category.hidden) return '';

  switch (category.id) {
    case 1: {
      const c1 = findCheck(category, 1);
      const c2 = findCheck(category, 2);
      const c5 = findCheck(category, 5);
      const parts = [];
      if (c1) parts.push(`${pct(c1.ratio_pct)} missing predecessors`);
      if (c2) parts.push(`${pct(c2.ratio_pct)} missing successors`);
      if (c5) parts.push(`${findingsCount(c5)} redundant relationships`);
      return truncate(parts.join(', '));
    }

    case 2: {
      const c7 = findCheck(category, 7);
      const c10 = findCheck(category, 10);
      const c11 = findCheck(category, 11);
      const parts = [];
      if (c7) parts.push(`${findingsCount(c7)} hard constraints`);
      if (c10) parts.push(`${findingsCount(c10)} invalid past-date constraints`);
      if (c11) parts.push(`${findingsCount(c11)} conflicting with logic`);
      return truncate(parts.join(', '));
    }

    case 3: {
      // R-16 (v3.12): the sentence derives from check #20's ONE computed pair
      // (breakCount / segmentCount) — never from #22's path count, never from
      // finding counts, never inverted. The old "N CP breaks" tail (which
      // counted segment-member findings, not breaks) is gone.
      const c20 = findCheck(category, 20);
      const c23 = findCheck(category, 23);
      const cp = c20?.cp_analysis;
      const parts = [];
      if (cp && !cp.continuous) {
        parts.push(`Path discontinuous (${cp.segmentCount} segments)`);
      } else {
        parts.push('Path continuous');
      }
      if (c23 && findingsCount(c23) > 0) {
        parts.push(`${findingsCount(c23)} constraint-driven on the path`);
      }
      return truncate(parts.join(', '));
    }

    case 4: {
      const c16 = findCheck(category, 16);
      const c17 = findCheck(category, 17);
      const c18 = findCheck(category, 18);
      const parts = [];
      if (c16) parts.push(`${findingsCount(c16)} negative float activities`);
      if (c17) parts.push(`${findingsCount(c17)} near-critical`);
      if (c18) parts.push(`${findingsCount(c18)} excessive float`);
      return truncate(parts.join(', '));
    }

    case 5: {
      const c12 = findCheck(category, 12);
      const c13 = findCheck(category, 13);
      const c30 = findCheck(category, 30);
      const parts = [];
      if (c13) parts.push(`${findingsCount(c13)} activities >44 days`);
      if (c12) parts.push(`${findingsCount(c12)} zero-duration non-milestones`);
      if (c30 && findingsCount(c30) > 0) parts.push(`${findingsCount(c30)} empty WBS nodes`);
      return truncate(parts.join(', '));
    }

    case 6: {
      const c24 = findCheck(category, 24);
      const c25 = findCheck(category, 25);
      const c26 = findCheck(category, 26);
      const parts = [];
      if (c24 && findingsCount(c24) === 0) {
        parts.push('All calendars valid');
      } else if (c24) {
        parts.push(`${findingsCount(c24)} invalid calendars`);
      }
      if (c25) parts.push(`${findingsCount(c25)} inconsistent pairs`);
      if (c26) parts.push(`${findingsCount(c26)} behind data date`);
      return truncate(parts.join(', '));
    }

    case 7: {
      // BASELINE-SUB4 F4: the shared module is the SINGLE summariser for the
      // comparison category — the dashboard CategoryRow renders the identical
      // string (Rule 12). Deliberately NOT truncated: both surfaces must match
      // byte-for-byte, and the dashboard applies no MAX_LEN.
      return buildComparisonCategorySummary(category);
    }

    case 8: {
      const c36 = findCheck(category, 36);
      const c37 = findCheck(category, 37);
      const parts = [];
      if (c36) {
        // E2: ratio_pct is the canonical loaded%; unloaded = denominator − numerator.
        // STAGE4-FIX (D-CatSummaryClip): the wide "Resource loading at N%
        // (N unloaded activities)" phrasing pushed the full three-fact summary
        // past the 85-char budget, so truncate() baked an ellipsis into the
        // PDF text layer ("…1 unused ca…"). Recomposed tighter — all three
        // facts fit; MAX_LEN stays as a last-resort guard only.
        const loaded = c36.ratio_pct ?? 0;
        const unloaded = (c36.denominator || 0) - (c36.numerator || 0);
        parts.push(unloaded > 0
          ? `${pct(loaded)} resource-loaded (${unloaded} unloaded)`
          : `${pct(loaded)} resource-loaded`);
      }
      if (c37) {
        // MEGAC-FIX F5: count-aware split — an unused calendar is NOT a ghost
        // reference. Ghosts = every finding that is not an unused calendar.
        const fs37 = c37.severity_findings || [];
        const unused = fs37.filter((f) => f.details?.ghostType === 'UNUSED_CALENDAR').length;
        const ghosts = fs37.length - unused;
        parts.push(ghosts === 0
          ? 'no corruption ghosts'
          : `${ghosts} ghost reference${ghosts === 1 ? '' : 's'}`);
        if (unused > 0) parts.push(`${unused} unused calendar${unused === 1 ? '' : 's'}`);
      }
      return truncate(parts.join(', '));
    }

    default:
      return '';
  }
}
